import { useEffect, useRef, useState } from 'react'
import { FocusTrapDialog } from '../FocusTrapDialog'
import { InlineAlert } from '../InlineAlert'
import { EntityRef } from '../EntityRef'
import { Icon } from '../icons/Icon'
import { WidgetErrorBoundary } from '../../screens/insights/widgets/WidgetErrorBoundary'
import { useDebouncedValue } from '../../lib/useDebouncedValue'
import { runGlobalSearch, SCAN_LIMIT, type SearchGroupResult } from '../../lib/globalSearch'
import './GlobalSearchOverlay.css'

export interface GlobalSearchOverlayProps {
  onClose: () => void
}

const MIN_TERM_LENGTH = 2

function groupDisclosure(group: SearchGroupResult): string {
  const count = group.items.length
  const noun = count === 1 ? 'result' : 'results'
  if (group.scanScope === 'recent') {
    const more = group.hasMore ? ' — more may match; refine your search' : ''
    return `${count} ${noun} (searched the ${SCAN_LIMIT} most recent)${more}`
  }
  return group.hasMore ? `${count} ${noun} — more available, refine your search` : `${count} ${noun}`
}

/** One group's result rows — isolated by `WidgetErrorBoundary` so a render bug in one group never blanks the rest. */
function SearchGroup({ group, onNavigate }: { group: SearchGroupResult; onNavigate: () => void }) {
  if (group.error) {
    return (
      <section className="iw-search-group" aria-label={group.groupLabel}>
        <h3 className="iw-search-group__heading">{group.groupLabel}</h3>
        <InlineAlert severity="error">Could not search {group.groupLabel.toLowerCase()}: {group.error}</InlineAlert>
      </section>
    )
  }
  if (group.items.length === 0) return null
  return (
    <section className="iw-search-group" aria-label={group.groupLabel}>
      <h3 className="iw-search-group__heading">
        {group.groupLabel}
        <span className="iw-search-group__count">{groupDisclosure(group)}</span>
      </h3>
      <ul className="iw-search-group__list">
        {group.items.map((item) => (
          <li key={`${item.kind}-${item.id}`} className="iw-search-result">
            <EntityRef kind={item.kind} id={item.id} name={item.label} href={item.href} onNavigate={onNavigate} />
            {item.secondary ? <span className="iw-search-result__secondary">{item.secondary}</span> : null}
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * Top-nav Global Search — a command-palette-style overlay (CLAUDE.md §4 IA: searches across every
 * entity a consultant firm's cross-case lists already carry — Account · Legal entity · Case, plus
 * Contacts/Branches/Productions/Tasks). FEDERATED, not a real search index — see
 * `lib/globalSearch.ts`'s own doc comment for the honest "server-side RSQL vs bounded client scan"
 * breakdown per entity type. Every result renders via `EntityRef` (never a raw UUID) and links to a
 * real route — clicking a result closes the overlay (`onNavigate`) so the `Link` navigation is not
 * left stacked under a stale modal.
 */
export function GlobalSearchOverlay({ onClose }: GlobalSearchOverlayProps) {
  const [term, setTerm] = useState('')
  const debouncedTerm = useDebouncedValue(term, 300)
  const [groups, setGroups] = useState<SearchGroupResult[] | null>(null)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const requestIdRef = useRef(0)

  const trimmed = debouncedTerm.trim()
  const belowThreshold = trimmed.length > 0 && trimmed.length < MIN_TERM_LENGTH

  useEffect(() => {
    if (trimmed.length < MIN_TERM_LENGTH) {
      setGroups(null)
      setLoading(false)
      return
    }
    const controller = new AbortController()
    const requestId = ++requestIdRef.current
    setLoading(true)
    runGlobalSearch(trimmed, controller.signal)
      .then((result) => {
        if (requestIdRef.current !== requestId) return
        setGroups(result)
      })
      .catch(() => {
        // runGlobalSearch isolates every per-group failure internally and never rejects; this catch
        // only guards against an unforeseen throw so the overlay still recovers to an empty result.
        if (requestIdRef.current !== requestId) return
        setGroups([])
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setLoading(false)
      })
    return () => controller.abort()
  }, [trimmed])

  const totalResults = groups?.reduce((sum, g) => sum + g.items.length, 0) ?? 0
  const anyErrors = groups?.some((g) => g.error) ?? false

  return (
    <FocusTrapDialog titleId="global-search-title" title="Search" onClose={onClose} initialFocusRef={inputRef} width={640}>
      <div className="iw-global-search">
        <div className="iw-global-search__input-wrap">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            type="search"
            className="iw-input iw-global-search__input"
            aria-label="Search"
            placeholder="Search cases, accounts, legal entities, contacts, branches, productions, tasks…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            aria-describedby="global-search-help"
          />
        </div>
        <p id="global-search-help" className="iw-global-search__help">
          Type at least {MIN_TERM_LENGTH} characters. Cases/Productions/Tasks search the {SCAN_LIMIT} most recently
          created records; every other type searches the full tenant.
        </p>

        <div className="iw-global-search__results" role="region" aria-live="polite">
          {trimmed.length === 0 ? null : belowThreshold ? (
            <p className="iw-global-search__hint">Keep typing — at least {MIN_TERM_LENGTH} characters to search.</p>
          ) : loading && groups === null ? (
            <p className="iw-global-search__hint">Searching…</p>
          ) : groups === null ? null : totalResults === 0 && !anyErrors ? (
            <div className="iw-global-search__empty">
              <Icon name="search" size={20} />
              <p>No matches for “{trimmed}”.</p>
              <p className="iw-global-search__hint">Try a different term, or check the spelling.</p>
            </div>
          ) : (
            groups.map((group) => (
              <WidgetErrorBoundary key={group.kind} title={group.groupLabel} icon="search">
                <SearchGroup group={group} onNavigate={onClose} />
              </WidgetErrorBoundary>
            ))
          )}
        </div>
      </div>
    </FocusTrapDialog>
  )
}
